import { useEffect, useRef, useState } from "react";
import {
  Box,
  Card,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { keyframes } from "@mui/system";
import EmptyTable from "src/components/emptyTable";
import Iconify from "src/components/Iconify";
import PaginationButtons from "src/components/pagination";
import Scrollbar from "src/components/Scrollbar";
import { Currency } from "src/components/with-prefix";
import useLocales from "src/hooks/useLocales";
import { fNumberCents } from "src/utils/formatNumber";

// Hint "scrolla a destra" su mobile: il container scrollabile e' interno a
// Scrollbar (su mobile = <Box overflowX:auto>, su desktop = SimpleBar).
// Lo cerchiamo a runtime dal wrapper, ascoltiamo scroll + resize e nascondiamo
// l'hint quando l'utente raggiunge la fine (o quando non c'e' overflow).
const SCROLL_END_THRESHOLD = 8;

const pulseX = keyframes`
  0%   { transform: translateX(0); opacity: 0.9; }
  50%  { transform: translateX(-4px); opacity: 1; }
  100% { transform: translateX(0); opacity: 0.9; }
`;

// Mappa payment_type DB -> { label i18n, tooltip i18n, color chip }.
// Coerente con AmountType.jsx e BreakdownByType.jsx.
const TYPE_MAP = {
  direct_unilevel_bonus:   { label: "userFinancial.eWallet.directUnilevelBonus",   tooltip: "userFinancial.eWallet.directUnilevelBonusTooltip",   color: "success" },
  indirect_unilevel_bonus: { label: "userFinancial.eWallet.indirectUnilevelBonus", tooltip: "userFinancial.eWallet.indirectUnilevelBonusTooltip", color: "info" },
  global_pool_bonus:       { label: "userFinancial.eWallet.globalPoolBonus",       tooltip: "userFinancial.eWallet.globalPoolBonusTooltip",       color: "secondary" },
  mother_bonus:            { label: "userFinancial.eWallet.motherBonus",           tooltip: "userFinancial.eWallet.motherBonusTooltip",           color: "warning" },
  mother_residual_bonus:   { label: "userFinancial.eWallet.motherResidualBonus",   tooltip: "userFinancial.eWallet.motherResidualBonusTooltip",   color: "error" },
  fund_transfer:           { label: "userFinancial.eWallet.fundTransfer",          tooltip: "userFinancial.eWallet.fundTransferTooltip",          color: "default" },
  credited_by_admin:       { label: "userFinancial.eWallet.creditedByAdmin",       tooltip: "userFinancial.eWallet.creditedByAdminTooltip",       color: "default" },
  deducted_by_admin:       { label: "userFinancial.eWallet.deductedByAdmin",       tooltip: "userFinancial.eWallet.deductedByAdminTooltip",       color: "default" },
};

const DataList = ({ data, rowStart, ...rest }) => {
  const { translate } = useLocales();
  const isEmpty = !Boolean(data?.length);

  const wrapperRef = useRef(null);
  const [showScrollHint, setShowScrollHint] = useState(false);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const root = wrapperRef.current;
    if (!root || isEmpty) {
      setShowScrollHint(false);
      return undefined;
    }
    const scrollEl =
      root.querySelector(".simplebar-content-wrapper") ||
      Array.from(root.querySelectorAll("div")).find((el) => {
        const cs = window.getComputedStyle(el);
        return cs.overflowX === "auto" || cs.overflowX === "scroll";
      });
    if (!scrollEl) return undefined;

    const update = () => {
      const hasOverflow = scrollEl.scrollWidth > scrollEl.clientWidth + 1;
      const atEnd = scrollEl.scrollLeft + scrollEl.clientWidth >= scrollEl.scrollWidth - SCROLL_END_THRESHOLD;
      setShowScrollHint(hasOverflow && !atEnd);
    };
    update();
    scrollEl.addEventListener("scroll", update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(scrollEl);
    return () => {
      scrollEl.removeEventListener("scroll", update);
      ro.disconnect();
    };
  }, [data, isEmpty]);

  // Visibilita' chevron fixed: solo quando la tabella e' effettivamente in
  // viewport, altrimenti la freccia "fluttua" anche quando l'utente ha gia'
  // scrollato oltre. threshold:0 = appena intersecata.
  useEffect(() => {
    const root = wrapperRef.current;
    if (!root) return undefined;
    const io = new IntersectionObserver(
      ([entry]) => setIsInView(entry.isIntersecting),
      { threshold: 0 },
    );
    io.observe(root);
    return () => io.disconnect();
  }, []);

  return (
    <>
      {/* No mt qui: il Grid container del parent gestisce gia' lo spacing,
          un mt extra disallinea il top rispetto alla card breakdown a fianco. */}
      <Box ref={wrapperRef} sx={{ position: "relative" }}>
      <Card>
        <Scrollbar>
          <TableContainer sx={{ minWidth: 640 }}>
            <Table size="small">
              <TableHead
                sx={{
                  // Header con padding aerato + niente bg attaccato al bordo.
                  // Le celle ereditano il bg trasparente della Card per
                  // evitare la "fascia grigia" attaccata al border-radius.
                  "& .MuiTableCell-head": {
                    backgroundColor: "transparent",
                    py: 1.5,
                    pt: 2.5,
                  },
                }}
              >
                <TableRow>
                  <TableCell>{translate("userFinancial.eWallet.date")}</TableCell>
                  <TableCell>{translate("userFinancial.eWallet.fromUser")}</TableCell>
                  <TableCell>{translate("userFinancial.eWallet.cause")}</TableCell>
                  <TableCell align="right">{translate("userFinancial.eWallet.amount")}</TableCell>
                </TableRow>
              </TableHead>
              {isEmpty ? (
                <TableBody>
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 5 }}>
                      <EmptyTable title={translate("userFinancial.eWallet.noTransactions")} />
                    </TableCell>
                  </TableRow>
                </TableBody>
              ) : (
                <TableBody>
                  {data.map((item) => {
                    // payment_type sul Model ha un accessor che traduce in label
                    // human; il valore raw e' esposto come payment_type_raw via $appends.
                    const rawType = item.payment_type_raw || item.payment_type;
                    const meta = TYPE_MAP[rawType] || { label: rawType, tooltip: null, color: "default" };
                    const isCredit = item.type === "credit";
                    const amountStr = fNumberCents(item.total_amount);

                    return (
                      <TableRow key={item.id} hover>
                        <TableCell>
                          {new Date(item.created_at).toLocaleDateString("en-GB")}
                        </TableCell>
                        <TableCell>{item.from_user?.username || "—"}</TableCell>
                        <TableCell>
                          {meta.tooltip ? (
                            <Tooltip title={translate(meta.tooltip)} arrow placement="top">
                              <Chip
                                label={translate(meta.label)}
                                size="small"
                                color={meta.color}
                                variant="outlined"
                                sx={{ cursor: "help", fontWeight: 600 }}
                              />
                            </Tooltip>
                          ) : (
                            <Chip
                              label={translate(meta.label)}
                              size="small"
                              color={meta.color}
                              variant="outlined"
                              sx={{ fontWeight: 600 }}
                            />
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 700, color: isCredit ? "success.main" : "error.main" }}
                          >
                            {isCredit ? "+" : "−"}
                            <Currency>{amountStr}</Currency>
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              )}
            </Table>
          </TableContainer>
        </Scrollbar>
      </Card>
        {/* Hint scrollabilita' orizzontale — solo mobile, solo se c'e' overflow.
            Fade gradient absolute full-height (coerenza visiva). */}
        <Box
          aria-hidden
          sx={{
            display: { xs: showScrollHint ? "block" : "none", sm: "none" },
            position: "absolute",
            top: 0,
            right: 0,
            bottom: 0,
            width: 56,
            pointerEvents: "none",
            borderTopRightRadius: (t) => t.shape.borderRadius * 2,
            borderBottomRightRadius: (t) => t.shape.borderRadius * 2,
            background: (t) =>
              `linear-gradient(to right, ${alpha(t.palette.background.paper, 0)} 0%, ${t.palette.background.paper} 70%)`,
          }}
        />
        {/* Chevron fixed al viewport: sempre visibile mentre l'utente scrolla
            verticalmente la pagina, finche' la tabella e' in vista E c'e' overflow. */}
        <Box
          aria-hidden
          sx={{
            display: {
              xs: showScrollHint && isInView ? "flex" : "none",
              sm: "none",
            },
            position: "fixed",
            top: "50%",
            right: 8,
            transform: "translateY(-50%)",
            width: 32,
            height: 32,
            borderRadius: "50%",
            backgroundColor: (t) => alpha(t.palette.background.paper, 0.9),
            boxShadow: 2,
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
            zIndex: 10,
          }}
        >
          <Iconify
            icon="mdi:chevron-right"
            sx={{
              width: 22,
              height: 22,
              color: "primary.main",
              animation: `${pulseX} 1.4s ease-in-out infinite`,
            }}
          />
        </Box>
      </Box>
      <Box sx={{ mt: 1 }}>
        <PaginationButtons {...rest} />
      </Box>
    </>
  );
};

export default DataList;
